import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Worker, Viewer, TextDirection } from "@react-pdf-viewer/core";
import {
    defaultLayoutPlugin,
    ToolbarProps,
    ToolbarSlot,
} from "@react-pdf-viewer/default-layout";
import { printPlugin } from "@react-pdf-viewer/print";
import { getFilePlugin } from "@react-pdf-viewer/get-file";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/print/lib/styles/index.css";

const IconDownload = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 20h14v-2H5v2zm7-18v10.17l3.59-3.58L17 10l-5 5-5-5 1.41-1.41L11 12.17V2h1z" />
    </svg>
);

const IconPrint = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 8H5c-1.66 0-3 1.34-3 3v4h4v4h12v-4h4v-4c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zM18 3H6v4h12V3z" />
    </svg>
);

/** Portal that injects buttons into the vertical tab rail */
const SidebarVerticalButtonsPortal: FC<{
    containerRef: React.RefObject<HTMLDivElement>;
    downloadNode: React.ReactNode;
    printNode: React.ReactNode;
}> = ({ containerRef, downloadNode, printNode }) => {
    const [host, setHost] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;

        const findRail = () =>
            root.querySelector<HTMLElement>(".rpv-default-layout__sidebar-tabs");

        let target = findRail();
        if (target) {
            setHost(target);
            return;
        }

        const mo = new MutationObserver(() => {
            const next = findRail();
            if (next) {
                setHost(next);
                mo.disconnect();
            }
        });
        mo.observe(root, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [containerRef]);

    if (!host) return null;

    const node = (
        <div className="pdf-vertical-actions">
            {downloadNode}
            {printNode}
        </div>
    );

    return ReactDOM.createPortal(node, host);
};

export const PdfViewerV1: FC<{ content: string }> = ({ content }) => {
    const contentType = "data:application/pdf;base64,";
    const fileUrl = content.startsWith(contentType) ? content : `${contentType}${content}`;
    const isArabic = false; // Set based on your language detection

    const viewerShellRef = useRef<HTMLDivElement>(null);

    const renderToolbar = (Toolbar: (props: ToolbarProps) => ReactElement) => {
        const [menuOpen, setMenuOpen] = React.useState(false);
        const isMobile = typeof window !== "undefined" ? window.innerWidth < 768 : false;

        return (
            <Toolbar>
                {(slots: ToolbarSlot) =>
                    isMobile ? (
                        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%", position: "relative", paddingRight: 6 }}>
                            <button
                                onClick={() => setMenuOpen(!menuOpen)}
                                style={{ background: "#fff", border: "1px solid #ccc", borderRadius: 6, padding: "4px 6px", cursor: "pointer" }}
                            >
                                ☰ Tools
                            </button>
                            {menuOpen && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 28,
                                        right: 0,
                                        background: "#fff",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                        borderRadius: 8,
                                        padding: 6,
                                        zIndex: 999,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 4,
                                        minWidth: 110,
                                    }}
                                >
                                    <slots.Download />
                                    <slots.Print />
                                    <slots.ShowSearchPopover />
                                    <slots.GoToFirstPage />
                                    <slots.GoToPreviousPage />
                                    <slots.CurrentPageInput />/<slots.NumberOfPages />
                                    <slots.GoToNextPage />
                                    <slots.GoToLastPage />
                                    <slots.ZoomOut />
                                    <slots.Zoom />
                                    <slots.ZoomIn />
                                    <slots.SwitchTheme />
                                    <slots.EnterFullScreen />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 6 }}>
                            <slots.ShowSearchPopover />
                            <slots.Download />
                            <slots.Print />
                            <slots.GoToFirstPage />
                            <slots.GoToPreviousPage />
                            <slots.CurrentPageInput />/<slots.NumberOfPages />
                            <slots.GoToNextPage />
                            <slots.GoToLastPage />
                            <slots.ZoomOut />
                            <slots.Zoom />
                            <slots.ZoomIn />
                            <slots.SwitchTheme />
                            <slots.EnterFullScreen />
                        </div>
                    )
                }
            </Toolbar>
        );
    };

    const print = printPlugin();
    const { Print } = print;
    const getFile = getFilePlugin();
    const { Download } = getFile;

    // Create vertical tab buttons
    const downloadNode = (
        <Download>
            {(dl) => (
                <button
                    className="pdf-vertical-tab-button"
                    title="Download"
                    onClick={dl.onClick}
                    aria-label="Download"
                >
                    <IconDownload />
                </button>
            )}
        </Download>
    );

    const printNode = (
        <Print>
            {(pr) => (
                <button
                    className="pdf-vertical-tab-button"
                    title="Print"
                    onClick={pr.onClick}
                    aria-label="Print"
                >
                    <IconPrint />
                </button>
            )}
        </Print>
    );

    const defaultLayoutPluginInstance = defaultLayoutPlugin({ renderToolbar });

    return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
            <style>{`
                .pdf-viewer-shell {
                    position: relative;
                    height: 460px;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar {
                    display: flex;
                    flex-direction: row;
                    height: 100%;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar-tabs {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 0;
                    box-sizing: border-box;
                }

                .pdf-viewer-shell .pdf-vertical-actions {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    margin-top: 6px;
                    width: 100%;
                }

                .pdf-viewer-shell .pdf-vertical-tab-button {
                    width: 28px;
                    height: 28px;
                    padding: 0;
                    margin: 0;
                    border: none;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s ease-in-out, transform 0.1s ease;
                }

                .pdf-viewer-shell .pdf-vertical-tab-button:hover {
                    background: #e5e7eb;
                    transform: scale(1.05);
                }

                .pdf-viewer-shell .pdf-vertical-tab-button:active {
                    background: #d1d5db;
                    transform: scale(0.96);
                }

                .pdf-viewer-shell .pdf-vertical-tab-button:focus-visible {
                    outline: 2px solid #93c5fd;
                    outline-offset: 2px;
                }

                .pdf-viewer-shell .pdf-vertical-tab-button svg {
                    width: 16px;
                    height: 16px;
                    fill: #374151;
                }

                [dir="rtl"] .pdf-viewer-shell .rpv-default-layout__sidebar {
                    flex-direction: row-reverse;
                    border-left: 1px solid #e5e7eb;
                    border-right: none;
                }

                [dir="rtl"] .pdf-viewer-shell .rpv-default-layout__sidebar-tabs {
                    border-left: 1px solid #e5e7eb;
                    border-right: none;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar-content {
                    background: #fff;
                    overflow: auto;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar {
                    background: #f9fafb;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar-tab {
                    border-radius: 6px;
                    transition: background 0.15s ease-in-out;
                }

                .pdf-viewer-shell .rpv-default-layout__sidebar-tab:hover {
                    background: #e5e7eb;
                }
            `}</style>

            <div ref={viewerShellRef} className="pdf-viewer-shell">
                <SidebarVerticalButtonsPortal
                    containerRef={viewerShellRef}
                    downloadNode={downloadNode}
                    printNode={printNode}
                />

                <Viewer
                    fileUrl={fileUrl}
                    theme={{ direction: isArabic ? TextDirection.RightToLeft : TextDirection.LeftToRight }}
                    plugins={[defaultLayoutPluginInstance, print, getFile]}
                    defaultScale={1}
                />
            </div>
        </Worker>
    );
};